<template>
  <div class="row">
    <card-default>
      <div
        slot="title"
        class="card-title">
        <div class="h5">
          Pages
        </div>
      </div>
      <div
        slot="body"
        class="row">
        <field-input
          v-model="searchString"
          name="searchString"
          label="Search"
          class="col-sm-3"
          @input="search"
        />
        <field-select2
          v-model="perPage"
          :options="['25', '50', '100']"
          :on-change="perPageChanged"
          name="perPage"
          label="Per page"
          class="col-sm-3"
        />
        <field-select2
          v-model="searchGroup"
          :options="groups"
          :on-change="searchGroupChanged"
          name="searchGroup"
          label="Group"
          class="col-sm-3"
          clearable
        />
        <div class="col-sm-3 my-auto text-right">
          <auth-link
            to="new"
            append
            class="btn btn-primary btn-micro float-right"
            permission="page-create"
          >
            New Page
          </auth-link>
        </div>
      </div>
      <div
        slot="body"
        class="row">
        <field-select2
          v-model="searchPublished"
          :options="searchPublishedOptions"
          :on-change="searchPublishedChanged"
          name="searchPublished"
          label="Published"
          class="col-sm-3"
          clearable
        />
        <field-select2
          v-model="sortDescendingDefault"
          :options="sortDescendingOptions"
          :on-change="sortDescendingChanged"
          name="sort"
          label="Sort"
          class="col-sm-3"
        />
        <field-select
          v-model="selectedLocale"
          :options="localeOptions"
          name="locale"
          label="Locale"
          class="col-sm-3"
        />
        <div class="col-sm-3 my-auto text-right">
          <auth-button
            v-if="exportPages.length === 0"
            :disabled="downloadInProgress"
            class="btn btn-primary btn-micro float-right"
            permission="developer"
            @click="downloadAllPages"
          >
            Export
          </auth-button>
          <vue-csv-downloader
            v-if="exportPages.length > 0"
            :data="exportPages"
            :fields="exportFields"
          >
            Download CSV
          </vue-csv-downloader>
          <br>
          <a
            v-if="exportPages.length > 0"
            :href="exportPagesUrl"
            download="export.json"
          >
            Download JSON
          </a>
        </div>
      </div>
      <table
        slot="body"
        class="table table-hover pages-list">
        <thead>
          <tr>
            <th>
              Page
            </th>
            <th>
              Slug
            </th>
            <th>
              Updated at
            </th>
            <th>
              Published
            </th>
            <th>
              Group
            </th>
            <th>
              Locale
            </th>
            <th/>
            <th/>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(page, index) in pages"
          >
            <tr
              :key="'p' + index"
              class="main-page-content"
              @click="updateShowAllPages(page)">
              <td>
                {{ page.name }}
              </td>
              <td
                v-for="(content, cIndex) in page.contents.slice(0, 1)"
                :key="index + '-' + cIndex"
              >
                {{ content.slug }}
              </td>
              <td>
                {{ page.updated_at }}
              </td>
              <td>
                <published-badge :publishable="page" />
              </td>
              <td>
                <span v-if="page.groups && page.groups[0]">
                  {{ page.groups[0].slug }}
                </span>
              </td>
              <td />
              <td />
              <td />
            </tr>
            <tr
              v-for="(content, cIndex) in page.contents"
              v-show="!selectedLocale || selectedLocale === content.language.code"
              :key="index + '-' + cIndex"
              :class="{ 'open': page.showAllPages }"
              class="all-pages-content"
            >
              <td>
                {{ content.name }}
              </td>
              <td>
                {{ content.slug }}
              </td>
              <td>
                {{ content.updated_at }}
              </td>
              <td>
                <published-badge :publishable="content" />
              </td>
              <td />
              <td>
                {{ content.language.code }}
              </td>
              <td class="text-right">
                <a
                  :href="getPageUrl(content)"
                  target="_blank"
                  class="text-nowrap">
                  <i class="fa fa-external-link"/>
                  View page
                </a>
              </td>
              <td class="text-right">
                <auth-link
                  :to="page.id.toString() + '/' + content.language_id.toString()"
                  append
                  permission="page-view"
                >
                  <div class="btn-group">
                    <auth-button
                      class="btn btn-primary btn-micro"
                      permission="page-view"
                    >
                      <span
                        aria-hidden="true"
                        class="fa fa-edit"/>
                    </auth-button>
                  </div>
                </auth-link>
              </td>
            </tr>
            <tr
              v-for="(language, lIndex) in activeSite.languages"
              v-show="!page.contentLanguages[language.id] && (!selectedLocale || selectedLocale === language.code)"
              :key="index + '-l' + lIndex"
              :class="{ 'open': page.showAllPages }"
              class="all-pages-content"
            >
              <td colspan="5"/>
              <td>{{ language.code }}</td>
              <td />
              <td class="text-right">
                <auth-link
                  :to="page.id.toString() + '/' + language.id.toString()"
                  append
                  permission="page-view"
                >
                  <div class="btn-group">
                    <auth-button
                      class="btn btn-primary btn-micro"
                      permission="page-view"
                    >
                      <span
                        aria-hidden="true"
                        class="fa fa-edit"/>
                    </auth-button>
                  </div>
                </auth-link>
              </td>
            </tr>
          </template>
        </tbody>
        <tfoot>
          <td colspan="999">
            <pagination
              :page-selected="pageSelected"
              :state="$store.state.page"
            />
          </td>
        </tfoot>
      </table>
    </card-default>
  </div>
</template>

<script>
import CardDefault from '@/components/CardDefault'
import FieldInput from '@/components/form/FieldInput'
import FieldSelect2 from '@/components/form/FieldSelect2'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { debounce } from 'lodash'
import VueCsvDownloader from 'vue-csv-downloader'
import PublishedBadge from './PublishedBadge'
import Pagination from '@/components/Pagination'
import FieldSelect from '@/components/form/FieldSelect'

export default {
  name: 'PagesList',

  components: {
    FieldInput,
    CardDefault,
    FieldSelect2,
    VueCsvDownloader,
    PublishedBadge,
    Pagination,
    FieldSelect
  },

  data () {
    return {
      pages: [],
      searchString: '',
      searchGroup: 0,
      searchPublished: '',
      selectedLocale: '',
      searchPublishedOptions: [{
        value: 'PUBLISHED',
        label: 'Published'
      }, {
        value: 'DRAFT',
        label: 'Draft'
      }],
      exportPages: [],
      exportFields: ['name', 'slug', 'title', 'description', 'keywords', 'ogLocale', 'ogTitle', 'ogDescription', 'ogType', 'ogImage', 'ogImageAlt', 'ogUrl', 'ogSiteName'],
      downloadInProgress: false,
      sortDescending: true,
      sortDescendingDefault: 'Descending',
      sortDescendingOptions: [
        {
          value: true,
          label: 'Descending'
        },
        {
          value: false,
          label: 'Ascending'
        }
      ]
    }
  },

  computed: {
    ...mapGetters('page', [
      'pagesArray',
      'currentPage',
      'totalPageCount',
      'perPage'
    ]),
    ...mapGetters({
      activeSite: 'site/active',
      groups: 'pageGroup/groupsSimple'
    }),
    getParams () {
      let params = {
        'per-page': this.perPage,
        'sort': this.sortDescending ? '-updated_at' : 'updated_at'
      }
      if (this.searchString) {
        params.name = this.searchString
        params.slug = this.searchString
      }
      if (this.searchGroup) {
        params['groupIds[]'] = this.searchGroup.value
      }
      if (this.searchPublished) {
        params.published = this.searchPublished.value
      }
      return params
    },
    exportPagesUrl () {
      return window.URL.createObjectURL(
        new Blob(
          [
            JSON.stringify(this.exportPages)
          ],
          {
            type: 'application/json'
          }
        )
      )
    },
    localeOptions () {
      return [{
        value: '',
        label: 'Remove filter'
      }].concat(this.activeSite.languages.map(locale => locale.code))
    }
  },

  created () {
    this.search()
    this.getGroups()
  },

  methods: {
    ...mapActions('page', [
      'get',
      'getAll'
    ]),
    ...mapActions({
      getGroups: 'pageGroup/get'
    }),
    ...mapMutations({
      setPerPage: 'page/SET_PER_PAGE',
      updateProp: 'page/UPDATE_PROP'
    }),
    getPageUrl (content) {
      return this.activeSite.json.url + content.language.code + '/' + content.slug
    },
    pageSelected (page) {
      const getParams = this.getParams
      getParams.page = page
      this.get(getParams).then(() => {
        this.pages = this.pagesArray
      })
    },
    search: debounce(function () {
      this.get(this.getParams).then(() => {
        this.pages = this.pagesArray
      })
    }, 300),
    perPageChanged (value) {
      this.setPerPage(value)
      this.search()
    },
    searchGroupChanged (value) {
      this.searchGroup = value
      this.search()
    },
    sortDescendingChanged ({ value }) {
      this.sortDescending = value
      this.pageSelected()
    },
    searchPublishedChanged (value) {
      this.searchPublished = value
      this.search()
    },
    downloadAllPages () {
      this.downloadInProgress = true
      this.getAll().then(allPages => {
        this.exportPages = allPages
        this.downloadInProgress = false
      })
    },
    updateShowAllPages (page) {
      this.updateProp({ page, prop: 'showAllPages', value: !page.showAllPages })
      this.$forceUpdate()
    }
  }
}
</script>
